/**
 * The Company-branded sign-in on fias.io (ADR-024 §4.4.1, S10).
 *
 * `/auth/company/:hostId/{sign-in,register,forgot}` render the SAME auth
 * pages as `/auth/login` etc., inside this provider. The pages read it for
 * three things only:
 *   - the brand band ("Sign in to Devv with your Fias account"), and
 *     "Secured by Fias";
 *   - where their links go, so the whole round trip stays branded and ends at
 *     the Continue step (`/auth/company/:hostId?…`);
 *   - whether registration is offered: an `open` portal offers "Sign in or
 *     create account", a `members_only` one offers sign-in only.
 * Credentials, MFA and recovery are exactly the platform's.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from 'next-themes';
import { onColor } from '@/lib/brand-color';
import { api, type CompanyHostBrandDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { brandPrimaryVars } from '@/lib/brand-theme';

export interface CompanyAuthBrand {
  hostId: string;
  hostname: string;
  /** The Company, as its people know it — the headline. */
  name: string;
  /** This portal's own name ("Employee portal"), shown beneath, when it adds anything. */
  portalLabel: string | null;
  companyName: string | null;
  logoDataUrl: string | null;
  primaryColor: string | null;
  headerTextColor: string | null;
  allowRegistration: boolean;
  paths: { signIn: string; register: string; forgot: string };
  /** Where the flow resumes — the Continue step with its authorization request. */
  continueTo: string;
}

const CompanyAuthBrandContext = createContext<CompanyAuthBrand | null>(null);

export function useCompanyAuthBrand(): CompanyAuthBrand | null {
  return useContext(CompanyAuthBrandContext);
}

export const COMPANY_REGISTERED_KEY = (hostId: string) => `fias.company-registered.${hostId}`;

/** Remembers, for this tab, that the account was just created in this portal's flow. */
export function markRegisteredHere(hostId: string) {
  try {
    window.sessionStorage.setItem(COMPANY_REGISTERED_KEY(hostId), '1');
  } catch {
    // Attribution is best effort.
  }
}

export function toCompanyAuthBrand(dto: CompanyHostBrandDto, continueTo: string): CompanyAuthBrand {
  const base = `/auth/company/${encodeURIComponent(dto.hostId)}`;
  const companyName = dto.brand?.displayName || dto.companyName;
  const name = companyName || dto.displayLabel || dto.hostname;
  return {
    hostId: dto.hostId,
    hostname: dto.hostname,
    name,
    portalLabel: dto.displayLabel && dto.displayLabel !== name ? dto.displayLabel : null,
    companyName,
    logoDataUrl: dto.brand?.logoDataUrl ?? null,
    primaryColor: dto.brand?.primaryColor ?? null,
    headerTextColor: dto.brand?.headerTextColor ?? null,
    allowRegistration: dto.accessPolicy === 'open',
    paths: { signIn: `${base}/sign-in`, register: `${base}/register`, forgot: `${base}/forgot` },
    continueTo,
  };
}

export function useCompanyHostBrand(hostId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.companyPortalSignIn.hostBrand(hostId ?? ''),
    queryFn: () => api.getCompanyHostBrand(hostId!),
    enabled: !!hostId,
    staleTime: 60_000,
    retry: false,
  });
}

export function CompanyAuthBrandProvider({
  brand,
  children,
}: {
  brand: CompanyAuthBrand;
  children: ReactNode;
}) {
  return (
    <CompanyAuthBrandContext.Provider value={brand}>
      <CompanyBrandTheme primaryColor={brand.primaryColor}>{children}</CompanyBrandTheme>
    </CompanyAuthBrandContext.Provider>
  );
}

/**
 * The Company's band above a branded auth card: its logo and name on its own
 * colour, the portal's name beneath, and one line saying what account this is.
 * Text on the band is computed from the band (or the Company's validated
 * header colour), so it is legible in either theme.
 */
export function CompanyAuthBrandHeader({ brand }: { brand: CompanyAuthBrand }) {
  const band = brand.primaryColor;
  const text = brand.headerTextColor ?? (band ? onColor(band) : undefined);
  return (
    <div className="mb-6 overflow-hidden rounded-xl border shadow-sm">
      <div
        className={`flex items-center gap-3 px-4 py-4 ${band ? '' : 'bg-muted'}`}
        style={band ? { backgroundColor: band, color: text } : undefined}
      >
        {brand.logoDataUrl && (
          <img
            src={brand.logoDataUrl}
            alt=""
            className="h-10 w-auto max-w-[140px] shrink-0 object-contain"
          />
        )}
        <div className="min-w-0 text-left">
          <p className="truncate text-lg font-semibold leading-tight">{brand.name}</p>
          {brand.portalLabel && (
            <p className="truncate text-sm leading-tight opacity-80">{brand.portalLabel}</p>
          )}
        </div>
      </div>
      <p className="bg-card px-4 py-2 text-xs text-muted-foreground">
        Use your Fias account — the same one for {brand.name} and anywhere on Fias.
      </p>
    </div>
  );
}

/**
 * Puts the Company's colour on the primary buttons and links inside, where it
 * stays legible in the current theme (`brandPrimaryVars`).
 */
export function CompanyBrandTheme({
  primaryColor,
  children,
}: {
  primaryColor: string | null;
  children: ReactNode;
}) {
  const { resolvedTheme } = useTheme();
  const vars = brandPrimaryVars(primaryColor, resolvedTheme === 'dark' ? 'dark' : 'light');
  return <div style={vars}>{children}</div>;
}

export function SecuredByFias() {
  return (
    <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
      Secured by
      <img src="/fias-logo.png" alt="Fias" className="h-4 w-auto dark:invert" />
    </p>
  );
}
